import { MarketName } from "@/constants/marketName"
import { consentCodeParsed } from "@/features/consents"
import { EnergyType, SubscriptionFunnelType } from "@/features/subscription"
import { getDefaultInputMethodFor } from "@/features/subscription/meterEligibility/utils/getDefaultInputMethodFor"
import { QuoteVariant } from "@/lib/flags/feature-flags.shared"
import { type JourneyType } from "@/scalars/journeyType"
import { Schedule } from "@/scalars/schedule"
import { UserStatus } from "../../types/userStatus"
import {
    type OnboardingAffiliateFormValues,
    type OnboardingCustomParamsFormValues,
    type OnboardingFormValues,
} from "../types/onboardingFormValues"

const DEFAULT_JOURNEY_TYPE: JourneyType = "CHANGE_OF_SUPPLIER"

const DEFAULT_ADDRESS = { fullAddress: "", addrCityLabel: "", addrNumStreet: "", addrPostCode: "" }

// Server-resolved feature flags
const DEFAULT_FEATURE_FLAGS = {
    featureFlags: {
        quoteVariant: QuoteVariant.LowInputCustomizable,
    },
}

// Affiliate
const DEFAULT_AFFILIATE: OnboardingAffiliateFormValues = {
    affiliateCode: null,
    affiliateSessionId: null,
    affiliateQueryParams: null,
    affiliateOrganisationSalesChannel: "DIRECT",
    affiliateOrganisationName: null,
}

// Customization parameters
const DEFAULT_CUSTOM_PARAMS: OnboardingCustomParamsFormValues = {
    availableFunnelTypes: [
        SubscriptionFunnelType.MonoEnergyElectricity,
        SubscriptionFunnelType.MonoEnergyGas,
        SubscriptionFunnelType.DualEnergy,
    ],
    availableJourneyTypes: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
    availableScheduleTypes: [{ value: Schedule.DIRECT_DEBIT }],
}

// Customer default values
const DEFAULT_CUSTOMER = {
    accountNumber: null,
    email: "",
    password: "",
    title: null,
    familyName: "",
    givenName: "",
    mobile: "",
    dateOfBirth: null,
    userStatus: UserStatus.NewClient,
    propertyId: null,
    propertyAddress: null,
    token: "",
    userId: "",
    billingFrequency: "YEARLY" as const,
}

const DEFAULT_CUSTOMER_BILLING = {
    billingName: "",
    billingAddress: null,
    isAddressBillingAddress: true,
    isVulnerable: false,
    iban: "",
    hasStoredIban: false,
    paymentDay: 8,
    scheduleType: Schedule.DIRECT_DEBIT,
}

const DEFAULT_PREFERENCES = {
    acceptedTAndC: false,
    meterConsent: false,
    consents: {
        [consentCodeParsed.relationalCommunication]: false,
        [consentCodeParsed.commercialCommunication]: false,
        [consentCodeParsed.personalCommunication]: true,
    },
    preferences: {
        isOptedInToSmsMessages: false,
    },
    requestedSupplyStartDate: null,
}

const DEFAULT_ELECTRICITY = {
    marketName: MarketName.FRA_ELECTRICITY,
    ledgerNumber: null,
    annualConsumptionReference: null,
    invoiceUpload: {
        amountDue: null,
        isInvoiceOld: null,
        extractionUuid: null,
    },
    consentData: {
        collectDailyConsumptionData: false,
        collectHalfHourlyConsumptionData: false,
    },
    providerCalendarId: null,
    isSeasonalHphcBase: false,
    quotedProduct: {
        monthlyAmount: 0,
        product: {
            code: "",
        },
    },
    quotedProducts: [],
    quotedSupplyPointId: null,
    readings: null,
    supplyPoint: {
        id: null,
        isEligible: null,
        hasChangeOfSupplierError: false,
        meterPoint: {
            id: "",
            serviceLevel: null,
            isLinky: null,
            isCommunicating: null,
            offPeakLabel: null,
            offPeakValues: [],
            providerCalendarId: null,
            providerCalendarName: null,
            subscribedMaxPower: null,
            poweredStatus: null,
            address: DEFAULT_ADDRESS,
            meterKind: null,
            isSensitive: null,
            circuitBreakerIntensity: null,
        },
    },
    yearlySaving: null,
    isSignificant: null,
    energyLandscapeDevicesEstimation: null,
    givenName: "",
    familyName: "",
}

// Gas
const DEFAULT_GAS = {
    marketName: MarketName.FRA_GAS,
    ledgerNumber: null,
    annualConsumptionReference: null,
    invoiceUpload: {
        amountDue: null,
        isInvoiceOld: null,
        extractionUuid: null,
    },
    consentData: {
        collectDailyConsumptionData: false,
    },
    quotedProduct: {
        monthlyAmount: 0,
        product: {
            code: "",
        },
    },
    quotedProducts: [],
    quotedSupplyPointId: null,
    readings: null,
    supplyPoint: {
        id: null,
        isEligible: null,
        hasChangeOfSupplierError: false,
        meterPoint: {
            id: "",
            isSmartMeter: null,
            priceLevel: null,
            poweredStatus: null,
            address: DEFAULT_ADDRESS,
        },
    },
    energyLandscapeDevicesEstimation: null,
    givenName: "",
    familyName: "",
}

const DEFAULT_REWARD = {
    reward: {
        code: "",
        referredRewardAmount: null,
        referrerRewardAmount: null,
        referrerGivenName: null,
    },
}

const DEFAULT_METER_POWER_ANALYSIS = {
    pmaxAnalysisToken: null,
    meterPowerAnalysis: {
        errorCode: null,
        pmaxToken: null,
        suggestedPowerAdjustment: null,
        currentSubscribedMaxPower: null,
        recommendedSubscribedMaxPower: null,
        yearlyProfitAmountCents: null,
        meterPointId: "",
    },
}

const DEFAULT_USER_INPUT = {
    electricitySubscribedMaxPower: null,
    energyLandscapeParameters: null,
} as const

const DEFAULT_ELECTRIC_VEHICLE = {
    hasElectricVehicle: false,
    hasElectricVehicleToggle: true,
    // Ford partnership purpose only
    vehicleIdentificationNumber: "",
    // IO journey purpose only
    averageAnnualMileage: null,
    averageVehicleConsumption: null,
}

const DEFAULT_ENERGY_LANDSCAPE = {
    energyLandscapeId: null,
}

const DEFAULT_MOVE_OUT = {
    moveOut: null,
    requestedSupplyEndDate: null,
}

const DEFAULT_OPEN_BANKING = {
    meeloOpenBankingUrl: null,
    riskAssessment: null,
    securityDepositReason: null,
}

const DEFAULT_CONFIG = {
    headless: false,
}

export const ONBOARDING_DEFAULT_FORM_VALUES: OnboardingFormValues = {
    partnerSlug: null,
    accountCampaignSlug: null,
    quoteCode: "",
    optimizedQuoteCode: null,
    journeyId: null,
    journeyType: DEFAULT_JOURNEY_TYPE,
    subscriptionType: "DEFAULT",
    selectedEnergyTypes: [EnergyType.Electricity],
    inputMethod: getDefaultInputMethodFor(DEFAULT_JOURNEY_TYPE),
    electricity: DEFAULT_ELECTRICITY,
    gas: DEFAULT_GAS,
    userInput: DEFAULT_USER_INPUT,
    riskLevel: "LOW",
    depositType: null,
    joinSupplierProcessNumber: null,
    enedisAnalysisToken: null,
    effy: { code: null, prefilledConsumption: null },
    ...DEFAULT_AFFILIATE,
    ...DEFAULT_CUSTOM_PARAMS,
    ...DEFAULT_FEATURE_FLAGS,
    ...DEFAULT_CUSTOMER,
    ...DEFAULT_CUSTOMER_BILLING,
    ...DEFAULT_METER_POWER_ANALYSIS,
    ...DEFAULT_ELECTRIC_VEHICLE,
    ...DEFAULT_PREFERENCES,
    ...DEFAULT_REWARD,
    ...DEFAULT_ENERGY_LANDSCAPE,
    ...DEFAULT_MOVE_OUT,
    ...DEFAULT_OPEN_BANKING,
    ...DEFAULT_CONFIG,
}
